import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { looksLikeInviteCode } from '../utils/inviteCodes';

import { auth, googleProvider, isFirebaseConfigured } from '../firebase/config';
import { useAuthStore } from '../store/authStore';
import { useChildStore } from '../store/childStore';
import { useT } from '../i18n';
import { trackEvent } from '../lib/analytics';
import { SEOHead } from '../components/blog/SEOHead';

// ─── Demo user ────────────────────────────────────────────────────────────────

const DEMO_USER = {
  uid:         'demo-user',
  email:       'demo@goodatoms.com',
  displayName: 'Demo Forelder',
  plan:        'free' as const,
  planExpiresAt: null,
  createdAt:   new Date(),
  language:    'no' as const,
};

// ─── Google G icon ────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

// ─── Left brand panel ─────────────────────────────────────────────────────────

function BrandPanel() {
  const { lang } = useT();

  const features = lang === 'en'
    ? [
        { icon: '⚡', text: 'Vibrant STEAM lessons for ages 4–16' },
        { icon: '🎯', text: 'Tailored to your child\'s age and interests' },
        { icon: '🖨',  text: 'Weekly Prnt Pack — printable worksheets' },
        { icon: '🌍', text: 'Available in Norwegian and English' },
      ]
    : [
        { icon: '⚡', text: 'Levende STEAM-leksjoner for 4–16 år' },
        { icon: '🎯', text: 'Tilpasset barnets alder og interesser' },
        { icon: '🖨',  text: 'Ukentlig Prnt Pack — arbeidsark til skriving' },
        { icon: '🌍', text: 'Tilgjengelig på norsk og engelsk' },
      ];

  return (
    <div className="hidden lg:flex flex-col justify-between h-full px-12 py-14">
      {/* Logo + tagline */}
      <div>
        <div className="good-atoms-wordmark text-teal text-3xl tracking-[0.22em] mb-2">Good Atoms</div>
        <p className="text-ink/50 text-sm font-medium">
          {lang === 'en' ? 'Learning that starts with wonder.' : 'Læring som begynner med undring.'}
        </p>
      </div>

      {/* Center content */}
      <div className="space-y-10">
        <div className="space-y-3">
          <p className="text-xs font-bold text-ink/50 uppercase tracking-widest">
            {lang === 'en' ? 'Why parents choose Good Atoms' : 'Hvorfor foreldre velger Good Atoms'}
          </p>
          <ul className="space-y-4">
            {features.map(({ icon, text }, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-lg leading-none mt-0.5">{icon}</span>
                <span className="text-ink/70 text-sm leading-relaxed font-medium">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Founding families — honest framing (replaces fabricated testimonial) */}
        <div className="rounded-2xl bg-[#F5EDD8]/70 border border-teal/25 p-5 space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal text-white text-[10px] font-extrabold tracking-wider uppercase">
              {lang === 'en' ? 'Founding families' : 'Founding-familier'}
            </span>
            <span className="text-[10px] text-ink/50 font-semibold">
              {lang === 'en' ? 'Limited to 100' : 'Begrenset til 100'}
            </span>
          </div>
          <p className="text-ink/75 text-sm leading-relaxed font-medium">
            {lang === 'en'
              ? "We're a new Norwegian platform. Become one of our first 100 families — everything free for 6 months, founding-member badge forever."
              : 'Vi er en ny norsk plattform. Bli en av våre første 100 familier — alt gratis i 6 måneder, founding member-badge for alltid.'}
          </p>
        </div>
      </div>

      {/* Mascots row */}
      <div className="flex items-center gap-4" aria-hidden="true">
        {['🟡', '🟢', '🔵', '🔴', '🟣'].map((emoji, i) => (
          <div key={i} className="w-10 h-10 rounded-xl bg-white/60 border border-ink/8 flex items-center justify-center">
            <span className="text-lg">{emoji}</span>
          </div>
        ))}
        <p className="text-ink/50 text-xs ml-1">
          {lang === 'en' ? '5 mascots · 6 subjects' : '5 maskotter · 6 fag'}
        </p>
      </div>
    </div>
  );
}

// ─── Input component ──────────────────────────────────────────────────────────

function Input({
  label, type = 'text', value, onChange, placeholder, autoComplete, error,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-ink/70">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full px-4 py-3 rounded-xl border text-ink text-sm placeholder-ink/25 outline-none transition-all bg-white/70 ${
          error
            ? 'border-red-300 ring-2 ring-red-100'
            : focused
            ? 'border-teal ring-2 ring-teal/15'
            : 'border-ink/15 hover:border-ink/25'
        }`}
      />
      {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type Mode = 'login' | 'signup';

export default function Login() {
  const navigate              = useNavigate();
  const [searchParams]        = useSearchParams();
  const { setUser, user }     = useAuthStore();
  const { activeChild }       = useChildStore();
  const { lang }              = useT();

  // ── If auth resolves (from any path), leave the login page ─────────────────
  // useAuth.ts loads children from Firestore BEFORE calling setUser, so by the
  // time this effect fires, activeChild is already populated for returning users.
  // → returning users (have children) → /home
  // → new users (no children yet)     → /onboarding/child
  useEffect(() => {
    if (user) navigate(activeChild ? '/home' : '/onboarding/child', { replace: true });
  }, [user, navigate, activeChild]);

  // ── Initial tab selection — Landing CTAs route to /login?signup=1 so first-
  //    time visitors land on the signup tab instead of a password field.
  const initialMode: Mode = searchParams.get('signup') === '1' ? 'signup' : 'login';

  const [mode,     setMode]     = useState<Mode>(initialMode);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [gLoading, setGLoading] = useState(false);
  const [errors,   setErrors]   = useState<Record<string, string>>({});
  const [globalErr, setGlobalErr] = useState('');
  const [inviteCode,    setInviteCode]    = useState('');
  const [inviteApplied, setInviteApplied] = useState(false);
  const [inviteOpen,    setInviteOpen]    = useState(false);
  const [forgotMode,    setForgotMode]    = useState(false);
  const [forgotEmail,   setForgotEmail]   = useState('');
  const [forgotSent,    setForgotSent]    = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotErr,     setForgotErr]     = useState('');

  // ── Validation ─────────────────────────────────────────────────────────────
  // Name dropped (captured later in ChildProfile onboarding).
  // Confirm-password dropped (single password field — industry standard for
  // free-trial signup, reduces friction).
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!email.includes('@') || !email.includes('.'))
      errs.email = lang === 'en' ? 'Invalid email address' : 'Ugyldig e-postadresse';
    if (mode === 'login') {
      // Login: just check non-empty (existing passwords may be shorter)
      if (password.length < 1)
        errs.password = lang === 'en' ? 'Please enter your password' : 'Vennligst skriv inn passordet ditt';
    } else {
      // Signup: enforce strong password
      if (password.length < 8)
        errs.password = lang === 'en' ? 'Password must be at least 8 characters' : 'Passord må ha minst 8 tegn';
      else if (!/[A-Z]/.test(password))
        errs.password = lang === 'en' ? 'Password must include an uppercase letter' : 'Passord må inneholde en stor bokstav';
      else if (!/[0-9]/.test(password))
        errs.password = lang === 'en' ? 'Password must include a number' : 'Passord må inneholde et tall';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Email login / signup ────────────────────────────────────────────────────
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalErr('');
    if (!validate()) return;
    setLoading(true);

    // Demo / no Firebase
    if (!isFirebaseConfigured()) {
      setUser({
        ...DEMO_USER,
        email,
        displayName: 'Forelder',
      });
      // user watcher above handles navigation
      return;
    }

    try {
      trackEvent(mode === 'login' ? 'signup_start' : 'signup_start', { method: 'email' });
      if (mode === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Store invite code for the auth listener to pick up (only if user
        // bothered to open + fill the collapsible invite section).
        if (inviteOpen && inviteCode.trim()) {
          sessionStorage.setItem('goodatoms_pending_invite', inviteCode.trim().toUpperCase());
        }
        await createUserWithEmailAndPassword(auth, email, password);
        trackEvent('signup_complete', { method: 'email', plan: 'free' });
      }
      // onAuthStateChanged in useAuth.ts loads children then calls setUser,
      // which triggers the user-watcher above to navigate appropriately.
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password')
        setGlobalErr(lang === 'en' ? 'Incorrect email or password. Try again.' : 'Feil e-post eller passord. Prøv igjen.');
      else if (code === 'auth/email-already-in-use')
        setGlobalErr(lang === 'en' ? 'This email is already registered. Log in instead.' : 'Denne e-posten er allerede registrert. Logg inn i stedet.');
      else if (code === 'auth/too-many-requests')
        setGlobalErr(lang === 'en' ? 'Too many attempts. Please wait and try again.' : 'For mange forsøk. Vent litt og prøv igjen.');
      else
        setGlobalErr(lang === 'en' ? 'Something went wrong. Try again.' : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  // ── Google ──────────────────────────────────────────────────────────────────
  // Strategy: signInWithPopup first (stays on page, no redirect race condition).
  // If popup is blocked by the browser, fall back to signInWithRedirect.
  // getRedirectResult is handled exclusively in useAuth.ts so it runs before
  // Login.tsx even mounts, avoiding the one-shot race condition.
  const handleGoogleLogin = async () => {
    setGlobalErr('');
    setGLoading(true);

    if (!isFirebaseConfigured()) {
      setUser(DEMO_USER);
      // user watcher handles navigation
      return;
    }

    try {
      trackEvent('signup_start', { method: 'google' });
      // Primary: popup — no page navigation, works in a single attempt
      await signInWithPopup(auth, googleProvider);
      trackEvent('signup_complete', { method: 'google', plan: 'free' });
      // onAuthStateChanged in useAuth.ts sets the user.
      // The user-watcher above handles navigation.
      // gLoading stays true until navigate() unmounts this component.
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';

      if (code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request') {
        // Popup was blocked by the browser — fall back to full-page redirect
        try {
          await signInWithRedirect(auth, googleProvider);
          // Page navigates away; gLoading intentionally stays true
        } catch {
          setGlobalErr(
            lang === 'en'
              ? 'Could not start Google sign-in. Try again.'
              : 'Kunne ikke starte Google-innlogging. Prøv igjen.'
          );
          setGLoading(false);
        }
      } else if (code === 'auth/popup-closed-by-user') {
        // User dismissed the popup — not an error
        setGLoading(false);
      } else {
        setGlobalErr(
          lang === 'en'
            ? 'Google sign-in failed. Please try again.'
            : 'Google-innlogging mislyktes. Prøv igjen.'
        );
        setGLoading(false);
      }
    }
  };

  // ── Demo ────────────────────────────────────────────────────────────────────
  const handleDemo = () => {
    setUser(DEMO_USER);
    // user watcher handles navigation
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setErrors({});
    setGlobalErr('');
    setInviteCode('');
    setInviteApplied(false);
    setInviteOpen(false);
  };

  // ── Forgot password ──────────────────────────────────────────────────────────
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotErr('');
    if (!forgotEmail.includes('@') || !forgotEmail.includes('.')) {
      setForgotErr(lang === 'en' ? 'Please enter a valid email address.' : 'Skriv inn en gyldig e-postadresse.');
      return;
    }
    setForgotLoading(true);
    try {
      if (!isFirebaseConfigured()) {
        // Demo mode — pretend it works
        await new Promise((r) => setTimeout(r, 800));
        setForgotSent(true);
        return;
      }
      await sendPasswordResetEmail(auth, forgotEmail);
      setForgotSent(true);
    } catch {
      setForgotErr(lang === 'en' ? 'Could not send reset email. Check the address and try again.' : 'Kunne ikke sende tilbakestillingslenke. Sjekk adressen og prøv igjen.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex">
      <SEOHead
        meta={{
          title: lang === 'en' ? 'Log in or sign up' : 'Logg inn eller opprett konto',
          description: lang === 'en'
            ? 'Log in to Good Atoms or create a free account. STEAM lessons for children aged 4–16, in Norwegian and English.'
            : 'Logg inn på Good Atoms eller opprett en gratis konto. STEAM-leksjoner for barn 4–16 år, på norsk og engelsk.',
          url: 'https://goodatoms.com/login',
          locale: lang === 'en' ? 'en' : 'nb_NO',
        }}
      />
      {/* ── Left brand panel (desktop only) ── */}
      <div className="hidden lg:block w-[420px] shrink-0 bg-gradient-to-br from-teal/8 via-page to-[#6366F1]/5 border-r border-ink/6">
        <BrandPanel />
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="good-atoms-wordmark text-teal text-3xl tracking-[0.22em] mb-1">Good Atoms</div>
            <p className="text-ink/50 text-sm">
              {lang === 'en' ? 'Learning that starts with wonder.' : 'Læring som begynner med undring.'}
            </p>
          </div>

          <div className="space-y-6">
            {/* ── FORGOT PASSWORD MODE ─────────────────────────────────── */}
            {forgotMode ? (
              <div className="space-y-5">
                <div>
                  <h1 className="text-2xl font-bold text-ink">
                    {lang === 'en' ? 'Reset password' : 'Tilbakestill passord'}
                  </h1>
                  <p className="text-ink/50 text-sm mt-1">
                    {lang === 'en'
                      ? "We'll send a reset link to your email address."
                      : 'Vi sender en tilbakestillingslenke til e-postadressen din.'}
                  </p>
                </div>

                {forgotSent ? (
                  <div className="px-4 py-5 rounded-xl bg-teal/8 border border-teal/20 text-center space-y-2">
                    <p className="text-2xl" aria-hidden="true">📬</p>
                    <p className="text-sm font-semibold text-ink">
                      {lang === 'en' ? 'Check your inbox!' : 'Sjekk innboksen din!'}
                    </p>
                    <p className="text-xs text-ink/50">
                      {lang === 'en'
                        ? `We sent a reset link to ${forgotEmail}. Check your spam folder if you don't see it.`
                        : `Vi sendte en lenke til ${forgotEmail}. Sjekk søppelpost-mappen om du ikke ser den.`}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    {forgotErr && (
                      <p className="text-xs text-red-500 font-medium">{forgotErr}</p>
                    )}
                    <Input
                      label={lang === 'en' ? 'Email address' : 'E-postadresse'}
                      type="email"
                      value={forgotEmail}
                      onChange={setForgotEmail}
                      placeholder={lang === 'en' ? 'you@example.com' : 'deg@eksempel.no'}
                      autoComplete="email"
                    />
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full py-3 rounded-xl bg-teal text-white font-bold text-sm hover:bg-teal/90 active:scale-[0.98] transition-all shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {forgotLoading ? (
                        <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      ) : (
                        lang === 'en' ? 'Send reset link →' : 'Send tilbakestillingslenke →'
                      )}
                    </button>
                  </form>
                )}

                <button
                  type="button"
                  onClick={() => setForgotMode(false)}
                  className="w-full text-xs text-ink/50 hover:text-teal transition-colors font-medium text-center"
                >
                  {lang === 'en' ? '← Back to log in' : '← Tilbake til innlogging'}
                </button>
              </div>
            ) : (
            <>
            {/* Heading */}
            <div>
              <h1 className="text-2xl font-bold text-ink">
                {mode === 'login'
                  ? (lang === 'en' ? 'Log in' : 'Logg inn')
                  : (lang === 'en' ? 'Create account' : 'Opprett konto')}
              </h1>
              <p className="text-ink/50 text-sm mt-1">
                {mode === 'login'
                  ? (lang === 'en' ? 'Welcome back! Log in to continue.' : 'Velkommen tilbake! Logg inn for å fortsette.')
                  : (lang === 'en' ? 'Get started free — no credit card required.' : 'Kom i gang gratis — ingen kredittkort nødvendig.')}
              </p>
            </div>

            {/* Mode tabs */}
            <div className="flex rounded-xl bg-ink/5 p-1 gap-1">
              {(['login', 'signup'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => switchMode(m)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mode === m
                      ? 'bg-white shadow-sm text-ink'
                      : 'text-ink/50 hover:text-ink/60'
                  }`}
                >
                  {m === 'login'
                    ? (lang === 'en' ? 'Log in' : 'Logg inn')
                    : (lang === 'en' ? 'Sign up' : 'Opprett konto')}
                </button>
              ))}
            </div>

            {/* Google button — primary path, visually emphasised for 1-click signup */}
            <button
              onClick={handleGoogleLogin}
              disabled={gLoading}
              className="w-full flex items-center justify-center gap-3 py-4 px-5 rounded-2xl border-2 border-ink/20 bg-white hover:bg-ink/3 hover:border-teal/40 hover:shadow-md transition-all text-base font-bold text-ink shadow-sm disabled:opacity-60"
            >
              {gLoading ? (
                <div className="w-6 h-6 rounded-full border-2 border-ink/15 border-t-ink/60 animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              {mode === 'login'
                ? (lang === 'en' ? 'Log in with Google' : 'Logg inn med Google')
                : (lang === 'en' ? 'Continue with Google' : 'Fortsett med Google')}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-ink/10" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-page text-ink/50 font-medium">
                  {lang === 'en' ? 'or with email' : 'eller med e-post'}
                </span>
              </div>
            </div>

            {/* Global error */}
            {globalErr && (
              <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
                {globalErr}
              </div>
            )}

            {/* Email form */}
              <form
                onSubmit={handleEmailSubmit}
                className="space-y-4"
              >
                {/* Name dropped — captured later in the ChildProfile onboarding step.
                    Confirm-password dropped — one field is the industry standard for
                    free-trial signup. */}
                <Input
                  label={lang === 'en' ? 'Email address' : 'E-postadresse'}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder={lang === 'en' ? 'you@example.com' : 'deg@eksempel.no'}
                  autoComplete="email"
                  error={errors.email}
                />

                <div className="space-y-1">
                  <Input
                    label={lang === 'en' ? 'Password' : 'Passord'}
                    type="password"
                    value={password}
                    onChange={setPassword}
                    placeholder={mode === 'login'
                      ? (lang === 'en' ? 'Your password' : 'Ditt passord')
                      : (lang === 'en' ? '8+ chars, uppercase & number' : '8+ tegn, stor bokstav & tall')}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    error={errors.password}
                  />
                  {mode === 'login' && (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => { setForgotMode(true); setForgotEmail(email); setForgotSent(false); setForgotErr(''); }}
                        className="text-xs text-teal hover:underline font-medium"
                      >
                        {lang === 'en' ? 'Forgot password?' : 'Glemt passord?'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Invite code (signup only) — collapsed by default to keep the
                    form short. Only 100 founding-member codes exist; most signups
                    won't have one. */}
                {mode === 'signup' && (
                  <div className="space-y-2">
                    {!inviteOpen ? (
                      <button
                        type="button"
                        onClick={() => setInviteOpen(true)}
                        className="text-xs text-teal hover:underline font-semibold"
                      >
                        {lang === 'en' ? 'Have an invite code?' : 'Har du en invitasjonskode?'}
                      </button>
                    ) : (
                      <div className="space-y-1">
                        <Input
                          label={lang === 'en' ? 'Invite code (optional)' : 'Invitasjonskode (valgfritt)'}
                          value={inviteCode}
                          onChange={(v) => {
                            setInviteCode(v);
                            setInviteApplied(looksLikeInviteCode(v));
                          }}
                          placeholder="GATM-XXXXXX"
                          autoComplete="off"
                        />
                        {inviteCode.trim() && (
                          <p className={`text-xs font-semibold ${inviteApplied ? 'text-teal' : 'text-red-400'}`}>
                            {inviteApplied
                              ? (lang === 'en' ? 'Code format looks good — we\'ll verify it on sign up!' : 'Kodeformat ser bra ut — vi bekrefter den ved registrering!')
                              : (lang === 'en' ? 'Code must be in GATM-XXXXXX format.' : 'Koden må være i GATM-XXXXXX-format.')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit — enlarged for visual prominence */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl bg-teal text-white font-extrabold text-base hover:bg-teal/90 active:scale-[0.98] transition-all shadow-md hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  ) : mode === 'login' ? (
                    lang === 'en' ? 'Log in →' : 'Logg inn →'
                  ) : (
                    lang === 'en' ? 'Create account →' : 'Opprett konto →'
                  )}
                </button>
              </form>

            {/* Demo link */}
            <div className="text-center">
              <button
                onClick={handleDemo}
                className="text-xs text-ink/50 hover:text-teal transition-colors font-medium underline underline-offset-2"
              >
                {lang === 'en' ? 'Try demo without account' : 'Prøv demo uten konto'}
              </button>
            </div>

            {/* Terms */}
            <p className="text-center text-ink/50 text-xs leading-relaxed">
              {lang === 'en' ? (
                <>
                  By continuing you agree to our{' '}
                  <button onClick={() => navigate('/terms')} className="underline hover:text-ink/60 transition-colors">terms</button>
                  {' '}and{' '}
                  <button onClick={() => navigate('/privacy')} className="underline hover:text-ink/60 transition-colors">privacy policy</button>.
                </>
              ) : (
                <>
                  Ved å fortsette godtar du våre{' '}
                  <button onClick={() => navigate('/terms')} className="underline hover:text-ink/60 transition-colors">vilkår</button>
                  {' '}og{' '}
                  <button onClick={() => navigate('/privacy')} className="underline hover:text-ink/60 transition-colors">personvernerklæring</button>.
                </>
              )}
            </p>
            </>
            )}
          </div>

          {/* Back link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-ink/50 hover:text-ink/60 text-xs transition-colors font-medium"
            >
              {lang === 'en' ? '← Back to home' : '← Tilbake til forsiden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
